import { create } from 'zustand'
import { POI_DATA_MAP } from '../config/poiDataMap'

// Re-export for backward compatibility
export { POI_DATA_MAP }
export type { POIDataConfig } from '../config/poiDataMap'

// Parsed CSV row
export type TelemetryRow = Record<string, number | string>

interface TelemetryState {
  rows: TelemetryRow[]
  headers: string[]
  isLoaded: boolean
  isLoading: boolean
  error: string | null

  loadCSV: () => Promise<void>
  getRowAtProgress: (progress: number) => TelemetryRow | null
  getTimeSeriesForPOI: (poiId: string) => { time: string; [key: string]: string | number }[]
  getSummaryAtProgress: (poiId: string, progress: number) => { label: string; value: string }[]
}

function parseCSV(text: string): { headers: string[]; rows: TelemetryRow[] } {
  const lines = text.split('\n').filter((l) => l.trim())
  // Row 3 (index 2) contains actual column headers
  const headerLine = lines[2]
  const headers = headerLine.split(',').map((h) => h.trim())

  const rows: TelemetryRow[] = []
  for (let i = 3; i < lines.length; i++) {
    const values = lines[i].split(',')
    const row: TelemetryRow = {}
    for (let j = 0; j < headers.length; j++) {
      const key = headers[j]
      if (!key) continue
      const raw = values[j]?.trim() ?? ''
      if (key === 'timestamp') {
        row[key] = raw
      } else {
        const num = parseFloat(raw)
        row[key] = isNaN(num) ? 0 : num
      }
    }
    rows.push(row)
  }

  return { headers: headers.filter(Boolean), rows }
}

export const useTelemetryStore = create<TelemetryState>((set, get) => ({
  rows: [],
  headers: [],
  isLoaded: false,
  isLoading: false,
  error: null,

  loadCSV: async () => {
    if (get().isLoaded || get().isLoading) return
    set({ isLoading: true })
    try {
      const res = await fetch('/data/2jan_data.csv')
      if (!res.ok) throw new Error('Failed to fetch CSV')
      const text = await res.text()
      const { headers, rows } = parseCSV(text)
      set({ headers, rows, isLoaded: true, isLoading: false })
    } catch (error) {
      console.error('Failed to load telemetry CSV:', error)
      set({ error: String(error), isLoading: false })
    }
  },

  getRowAtProgress: (progress: number) => {
    const { rows } = get()
    if (rows.length === 0) return null
    const offsetProgress = (progress + 0.25) % 1 // +6h offset to match TimeDisplay
    const index = Math.min(Math.floor(offsetProgress * (rows.length - 1)), rows.length - 1)
    return rows[index]
  },

  getTimeSeriesForPOI: (poiId: string) => {
    const { rows } = get()
    const config = POI_DATA_MAP.find((c) => c.poiId === poiId)
    if (!config || rows.length === 0) return []

    // Sample every 10 minutes to keep chart performant
    const sampled: { time: string; [key: string]: string | number }[] = []
    for (let i = 0; i < rows.length; i += 10) {
      const row = rows[i]
      const ts = row.timestamp as string
      const time = ts
        ? (ts.split('T')[1]?.slice(0, 5) ??
          `${Math.floor(i / 60)}:${String(i % 60).padStart(2, '0')}`)
        : ''
      const point: { time: string; [key: string]: string | number } = { time }
      for (const col of config.columns) {
        point[col.label] = (row[col.key] as number) ?? 0
      }
      sampled.push(point)
    }
    return sampled
  },

  getSummaryAtProgress: (poiId: string, progress: number) => {
    const config = POI_DATA_MAP.find((c) => c.poiId === poiId)
    if (!config) return []

    const row = get().getRowAtProgress(progress)
    if (!row) return []

    // For battery clusters, show avg SOC and total power
    if (config.aggregate === 'avg') {
      const socCols = config.columns.filter((c) => c.key.includes('_soc'))
      const powerCols = config.columns.filter((c) => c.key.includes('_power'))

      const avgSoc =
        socCols.length > 0
          ? socCols.reduce((sum, c) => sum + ((row[c.key] as number) ?? 0), 0) / socCols.length
          : 0
      const totalPower = powerCols.reduce((sum, c) => sum + ((row[c.key] as number) ?? 0), 0)

      return [
        { label: 'Avg SOC', value: `${avgSoc.toFixed(0)}%` },
        { label: 'Power', value: `${totalPower.toFixed(0)} kW` },
      ]
    }

    // For other POIs, show first 2 columns
    return config.columns.slice(0, 2).map((col) => ({
      label: col.label,
      value: `${((row[col.key] as number) ?? 0).toFixed(1)} ${col.unit}`,
    }))
  },
}))
